import { RecentlyViewed } from '@theme/recently-viewed-products';
import { formatMoney } from '@theme/money-formatting';

/**
 * Renders the products tracked by `RecentlyViewed` (see recently-viewed-products.js).
 * Fetches each product via Shopify's public `/products/{handle}.js` endpoint so no
 * app or Section Rendering round-trip is required — the tracked list lives only in
 * this browser's localStorage, so the render has to happen client-side either way.
 *
 * Stays `hidden` (set in the section markup) until at least one product renders, so a
 * first-time visitor with nothing tracked yet never sees an empty section.
 */
class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    this.#render();
  }

  async #render() {
    const list = this.querySelector('[ref="list"]');
    if (!list) return;

    const currentHandle = this.dataset.currentHandle || '';
    const max = Number.parseInt(this.dataset.max ?? '', 10) || 4;

    const entries = RecentlyViewed.getProducts()
      .filter((entry) => entry.handle && entry.handle !== currentHandle)
      .slice(0, max);

    if (entries.length === 0) return;

    const products = await Promise.all(
      entries.map((entry) =>
        fetch(`/products/${entry.handle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    );

    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const fragment = document.createDocumentFragment();

    for (const product of products) {
      if (!product) continue;

      const item = document.createElement('li');
      item.className = 'recently-viewed__item';

      const link = document.createElement('a');
      link.className = 'recently-viewed__link';
      link.href = product.url;

      const imageWrapper = document.createElement('span');
      imageWrapper.className = 'recently-viewed__image-wrapper';
      if (product.featured_image) {
        const img = document.createElement('img');
        img.className = 'recently-viewed__image';
        img.src = product.featured_image;
        img.loading = 'lazy';
        img.alt = product.title;
        imageWrapper.append(img);
      }

      const title = document.createElement('span');
      title.className = 'recently-viewed__title';
      title.textContent = product.title;

      const price = document.createElement('span');
      price.className = 'recently-viewed__price';
      const hasSale = typeof product.compare_at_price === 'number' && product.compare_at_price > product.price;

      const salePrice = document.createElement('span');
      salePrice.className = 'recently-viewed__sale-price';
      salePrice.textContent = formatMoney(product.price, moneyFormat, currency);
      price.append(salePrice);

      if (hasSale) {
        const comparePrice = document.createElement('span');
        comparePrice.className = 'recently-viewed__compare-price';
        comparePrice.textContent = formatMoney(product.compare_at_price, moneyFormat, currency);
        price.append(comparePrice);
      }

      link.append(imageWrapper, title, price);
      item.append(link);
      fragment.append(item);
    }

    if (!fragment.childElementCount) return;

    list.append(fragment);
    this.hidden = false;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
